/* eslint-disable react-hooks/exhaustive-deps */
import {useState, useRef, useEffect, useMemo} from 'react'
import {isEmpty} from '../../utils/common'
import {MultiSelectTreeDropDownTypes} from './MultiSelectTreeDropDownTypes'
import Loading from '../../modules/loading'

function MultiSelectTreeDropDown({
  id,
  treeData = [],
  placeholder = 'Select',
  labelKey = '',
  valueKey = 'id',
  defaultValue = [],
  onSubmit,
  label = '',
  isRequired = false,
  isDisable = false,
  isReadOnly = false,
  className = '',
  isLoading = false,
  isSearch = false,
  expandAllBtn = false,
  collapseAllBtn = false,
  childPadding = 10,
  returnKeys = ['id'],
  isDefaultExpandAll = false,
  isDisableExpand = false,
  isClearable = false,
  error,
  showTags = false,
  selectedValues= null,
}: MultiSelectTreeDropDownTypes) {
  const convertToNumber = (value: string | number) =>
    typeof value === 'string' ? Number(value) : value

  const findNodeById = (nodes: any[], itemId: number): any | null => {
    for (const node of nodes) {
      if (convertToNumber(node[valueKey]) === itemId) return node
      if (node.children) {
        const found = findNodeById(node.children, itemId)
        if (found) return found
      }
    }
    return null
  }

  const initialDefaultValue = useMemo(() => {
    if (Array.isArray(defaultValue) && typeof defaultValue[0] === 'object') {
      return defaultValue.map((item: any) => item[valueKey])
    }
    return defaultValue.map((item: any) => convertToNumber(item))
  }, [defaultValue, valueKey])

  const [isOpen, setIsOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState<number[]>(initialDefaultValue)
  const [tempSelectedItems, setTempSelectedItems] = useState<number[]>(initialDefaultValue)
  const [expandedNodes, setExpandedNodes] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isDefaultValueAssigned, setDefaultValueAssigned] = useState(false)
  const multiSelectTreeDropdownBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (
      !isEmpty(defaultValue) &&
      !isDefaultValueAssigned &&
      (selectedItems.length === 0 || tempSelectedItems.length === 0)
    ) {
      const numericDefaultValue = initialDefaultValue
      setSelectedItems(numericDefaultValue)
      setTempSelectedItems(numericDefaultValue)
      setDefaultValueAssigned(true)
    }
  }, [defaultValue, initialDefaultValue, isDefaultValueAssigned, selectedItems, tempSelectedItems])

  useEffect(() => {
    if (selectedValues !== null) {
      setSelectedItems(selectedValues)
      setTempSelectedItems(selectedValues)
    }
  }, [selectedValues])

  useEffect(() => {
    if (isDefaultExpandAll && !isEmpty(treeData)) {
      expandAll()
    }
  }, [isDefaultExpandAll, treeData])

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
    setTempSelectedItems(selectedItems)
  }

  const handleSelect = (id: string | number) => {
    const numericId = convertToNumber(id)
    const updatedItems = tempSelectedItems.includes(numericId)
      ? tempSelectedItems.filter((item) => item !== numericId)
      : [...tempSelectedItems, numericId]

    setTempSelectedItems(updatedItems)
    setSelectedItems(updatedItems)

    if (onSubmit) {
      const formatSelection = () => {
        if (returnKeys.includes('all')) {
          return updatedItems.map((item) => findNodeById(treeData, item)).filter(Boolean)
        }

        return updatedItems
          .map((item) => {
            const node = findNodeById(treeData, item)
            if (!node) return null

            if (returnKeys.length === 1) {
              return node[returnKeys[0]]
            }

            const selectedObj = returnKeys.reduce((acc: any, key: string) => {
              if (node[key] !== undefined) acc[key] = node[key]
              return acc
            }, {})

            return Object.keys(selectedObj).length > 0 ? selectedObj : null
          })
          .filter(Boolean)
      }

      onSubmit(formatSelection())
    }
  }

  const toggleNode = (id: string) => {
    setExpandedNodes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const expandAll = () => {
    const allNodes = getAllNodeIds(treeData)
    setExpandedNodes(allNodes)
  }

  const collapseAll = () => {
    setExpandedNodes([])
  }

  const getAllNodeIds = (nodes: any[]): string[] => {
    return nodes.reduce((acc: string[], node: any) => {
      if (node.children) {
        return [...acc, node[valueKey], ...getAllNodeIds(node.children)]
      }
      return [...acc, node[valueKey]]
    }, [])
  }

  const getSelectedNodeLabels = (nodes: any[], selectedIds: number[]): string[] => {
    return nodes.reduce((acc: string[], node: any) => {
      if (selectedIds.includes(Number(node[valueKey]))) {
        acc.push(node[labelKey])
      }
      if (node.children) {
        acc.push(...getSelectedNodeLabels(node.children, selectedIds))
      }
      return acc
    }, [])
  }

  const getSelectedLabels = useMemo((): string => {
    const selectedLabels = getSelectedNodeLabels(treeData, selectedItems)
    if (selectedLabels.length === 0) return placeholder

    const labelText = selectedLabels.join(', ')
    if (!multiSelectTreeDropdownBtnRef.current) {
      return labelText
    }

    const maxWidth = multiSelectTreeDropdownBtnRef.current.offsetWidth - 70
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (context) {
      const font = getComputedStyle(multiSelectTreeDropdownBtnRef.current).font
      context.font = font
      let truncatedText = labelText

      while (context.measureText(truncatedText).width > maxWidth && truncatedText.length > 0) {
        truncatedText = truncatedText.slice(0, -1)
      }

      return truncatedText.length < labelText.length ? truncatedText.trim() + '...' : labelText
    }

    return labelText
  }, [selectedItems, placeholder, treeData])

  const filterNodes = (nodes: any[], term: string, expandedNodeIds: string[]): any[] => {
    return nodes.reduce((acc: any[], node: any) => {
      if (node[labelKey].toLowerCase().includes(term.toLowerCase())) {
        acc.push(node)
      } else if (node.children) {
        const filteredChildren = filterNodes(node.children, term, expandedNodeIds)
        if (filteredChildren.length > 0) {
          acc.push({...node, children: filteredChildren})
          expandedNodeIds.push(node[valueKey])
        }
      }
      return acc
    }, [])
  }

  const filteredTreeData = useMemo(() => {
    const expandedNodeIds: string[] = []
    const filteredData = searchTerm ? filterNodes(treeData, searchTerm, expandedNodeIds) : treeData
    setExpandedNodes((prevExpandedNodes) => [
      ...prevExpandedNodes,
      ...expandedNodeIds.filter((id) => !prevExpandedNodes.includes(id)),
    ])
    return filteredData
  }, [searchTerm, treeData])

  const renderTreeNodes = (nodes: any[], isChildNode: boolean = false) => (
    <ul style={{paddingLeft: isChildNode ? `${childPadding}px` : '8px'}}>
      {nodes.map((node: any) => (
        <li
          key={node[valueKey]}
          className='border-top-1 border-gray-300 border-top-dashed list-unstyled'
        >
          <div className='d-flex align-items-center'>
            {node.children && (
              <button
                onClick={() => toggleNode(node[valueKey])}
                className={`btn btn-sm btn-link text-muted p-0 me-4`}
                aria-label={expandedNodes.includes(node[valueKey]) ? 'Collapse' : 'Expand'}
                type='button'
                disabled={isDisableExpand}
              >
                {expandedNodes.includes(node[valueKey]) ? (
                  <i className='bi bi-dash-circle fs-3 p-0'></i>
                ) : (
                  <i className='bi bi-plus-circle fs-3 p-0'></i>
                )}
              </button>
            )}
            {!node.children && <div style={{width: '2.30rem'}}></div>}
            <div className='form-check form-check-custom form-check-sm'>
              <input
                type='checkbox'
                id={node[valueKey]}
                checked={tempSelectedItems.includes(Number(node[valueKey]))}
                onChange={() => handleSelect(node[valueKey])}
                className='form-check-input'
              />
            </div>
            <label htmlFor={node[valueKey]} className='py-3 fs-6 ms-2 lh-1 cursor-pointer'>
              {node[labelKey]}
            </label>
          </div>
          {node.children &&
            expandedNodes.includes(node[valueKey]) &&
            renderTreeNodes(node.children, true)}
        </li>
      ))}
    </ul>
  )

  return (
    <div className={`position-relative ${className}`} id={`${id}-multi-select-tree`}>
      <label
        className={`form-label ${isRequired ? 'required' : ''}`}
        htmlFor={`${id}-multi-select-tree-dropdown-btn`}
      >
        {label}
      </label>
      {showTags && selectedItems.length > 0 ? (
        <div className='position-relative form-control p-2 mb-3'>
          <div className='d-flex flex-wrap gap-2 pe-5'>
            {selectedItems.length === 0 && <span className='text-muted'>{placeholder}</span>}
            {selectedItems.map((itemId) => {
              const node = findNodeById(treeData, itemId)
              if (!node) return null
              return (
                <div
                  key={itemId}
                  className='d-inline-flex align-items-center px-3 py-1 rounded bg-light text-primary'
                >
                  <span className='me-2'>{node[labelKey]}</span>
                  <button
                    type='button'
                    className='btn p-0 border-0 bg-transparent text-primary'
                    onClick={() => handleSelect(itemId)}
                  >
                    <i className='bi bi-x fs-6'></i>
                  </button>
                </div>
              )
            })}
          </div>
          {isClearable && (
            <i
              className='las la-times fs-4 text-muted cursor-pointer position-absolute top-50 end-0 translate-middle-y me-3'
              onClick={(e) => {
                e.stopPropagation()
                setTempSelectedItems([])
                setSelectedItems([])
                if (onSubmit) onSubmit([])
              }}
              aria-label='Clear selected items'
            ></i>
          )}
        </div>
      ) : (
        selectedItems &&
        selectedItems.length > 0 && (
          <button
            ref={multiSelectTreeDropdownBtnRef}
            onClick={toggleDropdown}
            className={`d-flex justify-content-between align-items-center form-control ${
              error ? 'is-invalid' : ''
            }`}
            style={{cursor: isReadOnly || isDisable ? 'default' : 'pointer'}}
            aria-haspopup='dialog'
            aria-expanded={isOpen ? 'true' : 'false'}
            type='button'
            id={`${id}-multi-select-tree-dropdown-btn`}
            disabled={isDisable}
          >
            {getSelectedLabels}
            {isClearable && selectedItems && selectedItems.length > 0 && (
              <i
                className='las la-times fs-4 ms-2 text-muted cursor-pointer'
                onClick={(e) => {
                  e.stopPropagation() // Prevent dropdown toggle
                  setTempSelectedItems([])
                  setSelectedItems([])
                  if (onSubmit) onSubmit([])
                }}
                aria-label='Clear selected items'
              ></i>
            )}
          </button>
        )
      )}

      <div className='p-4 border rounded mt-3'>
        <div className='position-relative'>
          {expandAllBtn || collapseAllBtn ? (
            <div className='d-flex justify-content-between mb-2'>
              {expandAllBtn && (
                <button type='button' onClick={expandAll} className='btn btn-outline-secondary'>
                  Expand All
                </button>
              )}
              {collapseAllBtn && (
                <button type='button' onClick={collapseAll} className='btn btn-outline-secondary'>
                  Collapse All
                </button>
              )}
            </div>
          ) : null}
          {isSearch && (
            <div className='mb-3'>
              <input
                type='text'
                placeholder='Search Category'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='form-control form-control-solid'
              />
            </div>
          )}
          <div className='pt-0 overflow-auto position-relative h-200px'>
            {renderTreeNodes(filteredTreeData, false)}
          </div>
          {isLoading && <Loading />}
        </div>
      </div>

      {error && <p className='invalid-feedback'>{error.message}</p>}
    </div>
  )
}

export default MultiSelectTreeDropDown
